/**
 * Rounds a monetary amount to a fixed number of decimal places using half-up
 * rounding, with a small epsilon guard against binary floating-point drift.
 *
 * Use this whenever persisted or displayed money must stay consistent to the
 * cent (e.g. quotation totals, deposits, ledger lines) so that
 * `net + tax === gross` after rounding.
 *
 * @param amount The value to round. `null` and `undefined` are treated as `0`.
 * @param decimalPlaces Number of fractional digits. Defaults to `2` (cents).
 * @returns The rounded amount as a number.
 * @example
 * ```ts
 * toRoundedAmount(10.005); // 10.01
 * ```
 * @example
 * ```ts
 * toRoundedAmount(33.333 * 0.15); // 5 (not 4.99995…)
 * ```
 * @example
 * ```ts
 * const net = toRoundedAmount(100 - 10);
 * const tax = toRoundedAmount(20 - 2);
 * const gross = toRoundedAmount(net + tax); // 108 — net + tax === gross
 * ```
 */
export function toRoundedAmount(
  amount: number | null | undefined,
  decimalPlaces = 2,
): number {
  const value = Number(amount ?? 0);
  const factor = 10 ** decimalPlaces;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}
